/**
 * YD Product Sequence
 * 产品序列帧动画模块 (275帧版本)
 *
 * 状态流程 (桌面端 - Scrub 模式):
 * LOADING → INTRO (0-50帧自动播放)
 *   → SCRUB (50-225帧 ScrollTrigger scrub)
 *   → FINAL_LOOP (225-274帧循环播放)
 *
 * 状态流程 (移动端 - 自动播放模式):
 * LOADING → INTRO (0-50帧自动播放)
 *   → INTRO_LOOP (50-75帧循环,等待用户滑动)
 *   → AUTO_PLAY (50→225帧自动播放)
 *   → FINAL_LOOP (225-274帧循环播放)
 */
(function() {
  'use strict';

  /**
   * 字符显现动画 - 随机闪烁效果
   * @param {Array<HTMLElement>} chars - 字符 span 数组
   * @param {Object} options - 配置选项
   * @returns {gsap.core.Tween} - GSAP 动画实例
   */
  function revealChars(chars, options = {}) {
    const { duration = 1.2, delay = 0, onComplete = null } = options;

    return gsap.to(chars, {
      keyframes: [
        { opacity: 1, color: '#333333', duration: 0 },
        { color: '#888888', duration: 0.05 },
        { color: '#444444', duration: 0.05 },
        { color: '#ffffff', duration: 0.2 }
      ],
      stagger: { amount: duration, from: 'random' },
      delay: delay,
      ease: 'power1.out',
      onComplete: onComplete
    });
  }

  /**
   * 本地序列帧加载器
   */
  class LocalSequenceLoader {
    constructor(config) {
      this.totalFrames = config.totalFrames;
      this.basePath = config.basePath;
      this.avifPath = config.avifPath;
      this.fallbackPath = config.fallbackPath;
      this.images = [];
      this.loadedCount = 0;
      this.actualFormat = null;
      this.priorityFrameCount = 0;
    }

    /**
     * 生成帧文件路径 - 使用5位补零格式 (1_00000)
     */
    getFramePath(frameNumber) {
      const paddedFrame = String(frameNumber).padStart(5, '0');
      const pathTemplate = this.actualFormat === 'avif' ? this.avifPath : this.fallbackPath;
      return this.basePath + pathTemplate.replace('[frame]', paddedFrame);
    }

    /**
     * 预加载优先帧
     */
    async preloadPriority(priorityFrames, onProgress) {
      this.images = new Array(this.totalFrames).fill(null);
      this.loadedCount = 0;
      this.priorityFrameCount = priorityFrames;

      // 检测 AVIF 支持
      const supportsAvif = window.imageFormatDetector
        ? await window.imageFormatDetector.isSupported('avif')
        : false;

      this.actualFormat = supportsAvif ? 'avif' : 'fallback';

      const loadPromises = [];
      for (let i = 0; i < priorityFrames; i++) {
        loadPromises.push(this.loadSingleFrame(i, onProgress, true));
      }

      const priorityImages = await Promise.all(loadPromises);
      priorityImages.forEach((img, index) => {
        this.images[index] = img;
      });
    }

    /**
     * 继续加载剩余帧
     */
    continuePreload(onProgress) {
      const remainingFrames = [];
      for (let i = 0; i < this.totalFrames; i++) {
        if (!this.images[i]) {
          remainingFrames.push(i);
        }
      }

      if (remainingFrames.length === 0) {
        return Promise.resolve();
      }

      const loadPromises = remainingFrames.map(frameNumber =>
        this.loadSingleFrame(frameNumber, onProgress).then(img => {
          this.images[frameNumber] = img;
          return img;
        })
      );

      return Promise.all(loadPromises)
        .catch(() => {});
    }

    /**
     * 加载单个帧
     */
    loadSingleFrame(frameNumber, onProgress, isPriority = false) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const framePath = this.getFramePath(frameNumber);

        img.onload = () => {
          this.loadedCount++;
          const percentage = isPriority
            ? this.loadedCount / this.priorityFrameCount
            : this.loadedCount / this.totalFrames;

          if (onProgress) {
            onProgress(percentage);
          }
          resolve(img);
        };

        img.onerror = () => {
                    reject(new Error('Failed to load frame ' + frameNumber + ': ' + framePath));
        };

        img.src = framePath;
      });
    }

    /**
     * 获取帧图片 (0-based 索引)
     */
    getFrame(frameNumber) {
      if (frameNumber < 0 || frameNumber >= this.images.length) {
                return null;
      }
      return this.images[frameNumber];
    }

    /**
     * 安全获取帧
     */
    getFrameSafe(frameNumber) {
      const img = this.getFrame(frameNumber);
      if (img) return img;

      // 查找最近的已加载帧
      for (let offset = 1; offset <= frameNumber; offset++) {
        const fallbackImg = this.getFrame(frameNumber - offset);
        if (fallbackImg) return fallbackImg;
      }

      for (let offset = 1; offset < this.totalFrames - frameNumber; offset++) {
        const fallbackImg = this.getFrame(frameNumber + offset);
        if (fallbackImg) return fallbackImg;
      }

      return null;
    }

    dispose() {
      this.images = [];
      this.loadedCount = 0;
    }
  }

  /**
   * Canvas 渲染器
   */
  class CanvasRenderer {
    constructor(canvasId) {
      this.canvas = document.querySelector(canvasId);
      if (!this.canvas) {
        throw new Error('Canvas not found: ' + canvasId);
      }

      this.ctx = this.canvas.getContext('2d');
      this.resizeCanvas();

      window.addEventListener('resize', () => this.resizeCanvas());
    }

    resizeCanvas() {
      // 移动端强制 DPR = 1，桌面端最大 2（性能优化）
      const isMobile = window.innerWidth < 768;
      const dpr = isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);
      const rect = this.canvas.getBoundingClientRect();

      this.canvas.width = rect.width * dpr;
      this.canvas.height = rect.height * dpr;

      this.ctx.scale(dpr, dpr);
      this.canvasWidth = rect.width;
      this.canvasHeight = rect.height;
    }

    /**
     * 渲染帧到 Canvas (cover 模式)
     */
    renderFrame(img) {
      if (!img) return;

      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      const imgAspect = img.width / img.height;
      const canvasAspect = this.canvasWidth / this.canvasHeight;

      let drawWidth, drawHeight, drawX, drawY;

      if (imgAspect > canvasAspect) {
        drawHeight = this.canvasHeight;
        drawWidth = this.canvasHeight * imgAspect;
        drawX = (this.canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = this.canvasWidth;
        drawHeight = this.canvasWidth / imgAspect;
        drawX = 0;
        drawY = (this.canvasHeight - drawHeight) / 2;
      }

      this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    }

    clear() {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    }
  }

  /**
   * 产品序列帧状态机 - 基类
   *
   * 共享桌面端和移动端的公共逻辑
   */
  class ProductSequenceStateMachineBase {
    constructor(config, loader, renderer, deviceType) {
      this.config = config;
      this.loader = loader;
      this.renderer = renderer;
      this.deviceType = deviceType;
      this.currentState = 'LOADING';
      this.currentFrame = 0;

      this.introTimeline = null;
      this.introLoopTimeline = null;
      this.finalLoopTimeline = null;
      this.scrollTrigger = null;

      // 标题元素引用
      this.titleUniverseTime = document.querySelector('#title-universe-time');
      this.titlePlayWithTime = document.querySelector('#title-play-with-time');
      this.titleFinalSides = document.querySelector('#title-final-sides');

      // 随机字符闪现动效状态
      this.finalTitlesAnimated = false;
      this.finalTitlesSplitInstances = [];
      this.finalTitlesChars = [];

      // Line Reveal 状态
      this.universeTimeRevealed = false;
      this.playWithTimeRevealed = false;
      this.splitTextInitialized = false;

      // 初始化
      this.splitFinalTitles();
      this.initSplitText();
    }

    /**
     * 日志前缀
     */
    get logPrefix() {
      return `[YD ProductSequence ${this.deviceType}]`;
    }

    /**
     * 拆分最终标题文字，准备随机字符闪现动效
     */
    splitFinalTitles() {
      if (typeof SplitText === 'undefined') {
                return;
      }

      const titles = document.querySelectorAll('.final-title');
      titles.forEach((title, index) => {
        const split = new SplitText(title, { type: 'chars' });
        this.finalTitlesSplitInstances.push(split);
        gsap.set(split.chars, { opacity: 0, color: '#333333' });
        this.finalTitlesChars[index] = split.chars;
      });
    }

    /**
     * 初始化 SplitText - 为标题准备 Line Reveal 动效
     */
    initSplitText() {
      if (this.splitTextInitialized) return;
      this.splitTextInitialized = true;

      // 分割 Universe Time
      if (this.titleUniverseTime) {
        this.splitUniverseTime = new SplitText(this.titleUniverseTime, {
          type: "lines",
          linesClass: "yd-line"
        });

        this.splitUniverseTime.lines.forEach(line => {
          const wrapper = document.createElement('div');
          wrapper.className = 'yd-line-inner';
          wrapper.innerHTML = line.innerHTML;
          line.innerHTML = '';
          line.appendChild(wrapper);
        });
      }

      // 分割 World's Only 720° 标题
      if (this.titlePlayWithTime) {
        this.splitPlayWithTime = new SplitText(this.titlePlayWithTime, {
          type: "lines",
          linesClass: "yd-line"
        });

        this.splitPlayWithTime.lines.forEach(line => {
          const wrapper = document.createElement('div');
          wrapper.className = 'yd-line-inner';
          wrapper.innerHTML = line.innerHTML;
          line.innerHTML = '';
          line.appendChild(wrapper);
        });
      }
    }

    /**
     * 播放最终标题随机字符闪现动效
     */
    animateFinalTitles() {
      if (this.finalTitlesAnimated) return;
      this.finalTitlesAnimated = true;

      this.finalTitlesChars.forEach((chars, index) => {
        if (!chars || chars.length === 0) return;
        revealChars(chars, { duration: 1.2, delay: index * 0.2 });
      });
    }

    /**
     * 重置最终标题动画状态
     */
    resetFinalTitlesAnimation() {
      if (!this.finalTitlesAnimated) return;
      this.finalTitlesAnimated = false;

      this.finalTitlesChars.forEach(chars => {
        if (chars && chars.length > 0) {
          gsap.set(chars, { opacity: 0, color: '#333333' });
        }
      });
    }

    /**
     * 解析滚动高度配置
     */
    parseScrollHeight(value) {
      const viewportHeight = window.innerHeight;
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && (value.endsWith('vh') || value.endsWith('svh'))) {
        return (parseFloat(value) / 100) * viewportHeight;
      }
      return viewportHeight * 2; // 默认 200svh
    }

    /**
     * 状态转换
     */
    transitionTo(newState) {
      this.cleanup();
      this.currentState = newState;

      switch (newState) {
        case 'INTRO':
          this.enterIntro();
          break;
        case 'INTRO_LOOP':
          this.enterIntroLoop();
          break;
        case 'SCRUB':
          this.enterScrub();
          break;
        case 'FINAL_LOOP':
          this.enterFinalLoop();
          break;
      }
    }

    /**
     * SCRUB 期间的标题动画 (基于 progress)
     */
    updateTitlesForScrub(progress) {
      const playWithTimeInners = this.titlePlayWithTime.querySelectorAll('.yd-line-inner');

      // Universe Time 淡出 (0-12.5%)
      if (progress < 0.125) {
        const fadeOut = progress / 0.125;
        gsap.set(this.titleUniverseTime, { opacity: 1 - fadeOut });
        gsap.set(this.titlePlayWithTime, { opacity: 0 });
      }
      // World's Only 720° 淡入 + Line Reveal (12.5-37.5%)
      else if (progress < 0.375) {
        const fadeIn = (progress - 0.125) / 0.25;
        gsap.set(this.titleUniverseTime, { opacity: 0 });
        gsap.set(this.titlePlayWithTime, { opacity: fadeIn });

        // Line Reveal 动画（只在首次触发）
        if (!this.playWithTimeRevealed && fadeIn > 0.1) {
          this.playWithTimeRevealed = true;

          gsap.fromTo(
            playWithTimeInners,
            { yPercent: 100 },
            { yPercent: 0, duration: 0.8, stagger: 0.12, ease: 'power3.out' }
          );
        }
      }
      // World's Only 720° 保持 (37.5-87.5%)
      else if (progress < 0.875) {
        gsap.set(this.titleUniverseTime, { opacity: 0 });
        gsap.set(this.titlePlayWithTime, { opacity: 1 });
      }
      // World's Only 720° 淡出 (87.5-100%)
      else {
        const fadeOut = (progress - 0.875) / 0.125;
        gsap.set(this.titleUniverseTime, { opacity: 0 });
        gsap.set(this.titlePlayWithTime, { opacity: 1 - fadeOut });
      }

      // 最终标题始终隐藏
      if (this.titleFinalSides) {
        gsap.set(this.titleFinalSides, { opacity: 0 });
      }
    }

    /**
     * 启动最终 LOOP 动画 (225-274 帧)
     */
    startFinalLoopAnimation() {
      const { finalLoopFrames } = this.config;
      const finalLoopDuration = (finalLoopFrames.end - finalLoopFrames.start) / 30;

      if (this.finalLoopTimeline) {
        this.finalLoopTimeline.kill();
      }

      this.finalLoopTimeline = gsap.timeline({
        repeat: -1,
        yoyo: false,
        onUpdate: () => {
          if (this.currentState === 'FINAL_LOOP') {
            const progress = this.finalLoopTimeline.progress();
            const frame = Math.floor(
              finalLoopFrames.start + progress * (finalLoopFrames.end - finalLoopFrames.start)
            );
            this.renderFrame(frame);
          }
        }
      });

      this.finalLoopTimeline.to({}, { duration: finalLoopDuration });
    }

    stopFinalLoopAnimation() {
      if (this.finalLoopTimeline) {
        this.finalLoopTimeline.kill();
        this.finalLoopTimeline = null;
      }
    }

    /**
     * 停止 INTRO_LOOP 动画
     */
    stopIntroLoopAnimation() {
      if (this.introLoopTimeline) {
        this.introLoopTimeline.kill();
        this.introLoopTimeline = null;
      }
    }

    renderFrame(frameNumber) {
      this.currentFrame = frameNumber;
      const img = this.loader.getFrameSafe(frameNumber);
      if (img) {
        this.renderer.renderFrame(img);
      }
    }

    /**
     * 清理当前状态的资源
     */
    cleanup() {
      this.playWithTimeRevealed = false;
      this.universeTimeRevealed = false;

      if (this.introTimeline) {
        this.introTimeline.kill();
        this.introTimeline = null;
      }

      this.stopIntroLoopAnimation();
    }

    dispose() {
      this.cleanup();
      this.stopFinalLoopAnimation();

      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }

      this.renderer.clear();
    }

    // 以下方法需要子类实现或覆盖
    enterIntro() {
      throw new Error('enterIntro must be implemented by subclass');
    }

    enterIntroLoop() {
      throw new Error('enterIntroLoop must be implemented by subclass');
    }

    enterScrub() {
      throw new Error('enterScrub must be implemented by subclass');
    }

    enterFinalLoop() {
      throw new Error('enterFinalLoop must be implemented by subclass');
    }
  }

  /**
   * 产品序列帧状态机 - 桌面端 Scrub 模式
   */
  class ProductSequenceStateMachineDesktop extends ProductSequenceStateMachineBase {
    constructor(config, loader, renderer) {
      super(config, loader, renderer, 'Desktop');
    }

    /**
     * INTRO 状态: 自动播放 0-50 帧，同时创建 ScrollTrigger (一开始就 pin)
     */
    enterIntro() {
      const { introFrames, introDuration, scrubFrames, scrollHeight, autoScrollSpeed, autoScrollIdleTimeout } = this.config;
      const scrollDistance = this.parseScrollHeight(scrollHeight);

      const wrapper = document.querySelector('#sequence-canvas-wrapper');

      // 记录上一次 progress，用于检测滚动方向
      let lastProgress = 0;

      // 创建 ScrollTrigger (pin)
      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距，避免手动设置高度导致的时序问题
      this.scrollTrigger = ScrollTrigger.create({
        trigger: wrapper,
        start: 'top top',
        end: `+=${scrollDistance}`,
        pin: wrapper,
        pinSpacing: true,
        scrub: 1,
        refreshPriority: 10,  // 最高优先级，确保先于下游 section 刷新
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const progress = self.progress;
          const direction = progress > lastProgress ? 1 : -1;

          if (this.currentState === 'INTRO_LOOP') {
            if (progress > 0.01 && direction > 0) {
                            this.transitionTo('SCRUB');
            }
            lastProgress = progress;
            return;
          }

          if (this.currentState === 'INTRO') {
            lastProgress = progress;
            return;
          }

          if (this.currentState === 'SCRUB') {
            if (progress < 0.01 && direction < 0) {
                            this.transitionTo('INTRO_LOOP');
              lastProgress = progress;
              return;
            }

            const { introLoopFrames } = this.config;
            const scrubStartFrame = introLoopFrames.end;
            const scrubEndFrame = scrubFrames.end;
            const frame = Math.floor(
              scrubStartFrame + progress * (scrubEndFrame - scrubStartFrame)
            );
            this.renderFrame(frame);
            this.updateTitlesForScrub(progress);
          }

          lastProgress = progress;
        },
        onLeave: () => {
          if (this.currentState === 'SCRUB') {
                        this.transitionTo('FINAL_LOOP');
          }
        },
        onEnterBack: () => {
                    if (this.currentState === 'FINAL_LOOP') {
            this.handleEnterBackFromFinalLoop(autoScrollSpeed, autoScrollIdleTimeout);
          }
        },
        onLeaveBack: () => {
          if (this.currentState === 'SCRUB') {
            this.transitionTo('INTRO_LOOP');
          }
        }
      });

      // 播放 INTRO 动画 (0-50帧)
      this.introTimeline = gsap.timeline({
        onUpdate: () => {
          const progress = this.introTimeline.progress();
          const frame = Math.floor(
            introFrames.start + progress * (introFrames.end - introFrames.start)
          );
          this.renderFrame(frame);
        },
        onComplete: () => {
          this.renderFrame(introFrames.end);
          this.playUniverseTimeReveal();
          this.transitionTo('INTRO_LOOP');
        }
      });

      this.introTimeline.to({}, { duration: introDuration });
    }

    /**
     * 从 FINAL_LOOP 回滚时的处理
     */
    handleEnterBackFromFinalLoop(autoScrollSpeed, autoScrollIdleTimeout) {
      this.stopFinalLoopAnimation();
      this.currentState = 'SCRUB';

      this.playWithTimeRevealed = false;
      this.resetFinalTitlesAnimation();

      if (this.titleFinalSides) {
        gsap.set(this.titleFinalSides, { opacity: 0 });
      }
    }

    /**
     * 播放 Universe Time 标题 Line Reveal
     */
    playUniverseTimeReveal() {
      const universeTimeInners = this.titleUniverseTime.querySelectorAll('.yd-line-inner');

      gsap.to(this.titleUniverseTime, {
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        onStart: () => {
          gsap.set(this.titlePlayWithTime, { opacity: 0 });
          if (this.titleFinalSides) {
            gsap.set(this.titleFinalSides, { opacity: 0 });
          }
        }
      });

      gsap.fromTo(
        universeTimeInners,
        { yPercent: 100 },
        { yPercent: 0, duration: 1.0, stagger: 0.12, ease: 'power3.out', delay: 0.1 }
      );
    }

    /**
     * INTRO_LOOP 状态
     */
    enterIntroLoop() {
      
      // 派发事件通知下游 section (video-scrub) 可以开始初始化
      window.dispatchEvent(new CustomEvent('yd-product-sequence-ready', {
        detail: { state: 'INTRO_LOOP', timestamp: Date.now(), device: 'desktop' }
      }));

      const { introLoopFrames, loopDuration } = this.config;

      this.playWithTimeRevealed = false;
      this.resetFinalTitlesAnimation();

      this.resetTitlesForIntroLoop();

      this.introLoopTimeline = gsap.timeline({
        repeat: -1,
        yoyo: false,
        onUpdate: () => {
          if (this.currentState === 'INTRO_LOOP') {
            const progress = this.introLoopTimeline.progress();
            const frame = Math.floor(
              introLoopFrames.start + progress * (introLoopFrames.end - introLoopFrames.start)
            );
            this.renderFrame(frame);
          }
        }
      });

      this.introLoopTimeline.to({}, { duration: loopDuration });

      this.loader.continuePreload();
    }

    /**
     * 重置标题到 INTRO_LOOP 状态
     */
    resetTitlesForIntroLoop() {
      if (this.titleUniverseTime) {
        gsap.set(this.titleUniverseTime, { opacity: 1 });
      }
      if (this.titlePlayWithTime) {
        gsap.set(this.titlePlayWithTime, { opacity: 0 });
        const playWithTimeInners = this.titlePlayWithTime.querySelectorAll('.yd-line-inner');
        gsap.set(playWithTimeInners, { yPercent: 100 });
      }
      if (this.titleFinalSides) {
        gsap.set(this.titleFinalSides, { opacity: 0 });
      }
    }

    /**
     * SCRUB 状态
     */
    enterScrub() {
      
      this.playWithTimeRevealed = false;
      this.universeTimeRevealed = false;
      this.resetFinalTitlesAnimation();
    }

    /**
     * FINAL_LOOP 状态
     */
    enterFinalLoop() {
      
      gsap.set(this.titleUniverseTime, { opacity: 0 });
      gsap.set(this.titlePlayWithTime, { opacity: 0 });

      if (this.titleFinalSides) {
        gsap.to(this.titleFinalSides, {
          opacity: 1,
          duration: 0.8,
          ease: 'power2.out',
          onComplete: () => this.animateFinalTitles()
        });
      }

      this.startFinalLoopAnimation();
    }
  }

  /**
   * 产品序列帧状态机 - 移动端 Scrub 模式
   */
  class ProductSequenceStateMachineMobile extends ProductSequenceStateMachineBase {
    constructor(config, loader, renderer) {
      super(config, loader, renderer, 'Mobile');
    }

    /**
     * INTRO 状态
     */
    enterIntro() {
      const { introFrames, introDuration, scrubFrames, scrollHeight, autoScrollSpeed, autoScrollIdleTimeout } = this.config;
      const scrollDistance = this.parseScrollHeight(scrollHeight);

      const wrapper = document.querySelector('#sequence-canvas-wrapper');

      let lastProgress = 0;

      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距，避免手动设置高度导致的时序问题
      this.scrollTrigger = ScrollTrigger.create({
        trigger: wrapper,
        start: 'top top',
        end: `+=${scrollDistance}`,
        pin: wrapper,
        pinSpacing: true,
        scrub: 1,
        refreshPriority: 10,  // 最高优先级，确保先于下游 section 刷新
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const progress = self.progress;
          const direction = progress > lastProgress ? 1 : -1;

          if (this.currentState === 'INTRO_LOOP') {
            if (progress > 0.01 && direction > 0) {
                            this.transitionTo('SCRUB');
            }
            lastProgress = progress;
            return;
          }

          if (this.currentState === 'INTRO') {
            lastProgress = progress;
            return;
          }

          if (this.currentState === 'SCRUB') {
            if (progress < 0.01 && direction < 0) {
                            this.transitionTo('INTRO_LOOP');
              lastProgress = progress;
              return;
            }

            const { introLoopFrames } = this.config;
            const scrubStartFrame = introLoopFrames.end;
            const scrubEndFrame = scrubFrames.end;
            const frame = Math.floor(
              scrubStartFrame + progress * (scrubEndFrame - scrubStartFrame)
            );
            this.renderFrame(frame);
            this.updateTitlesForScrub(progress);
          }

          lastProgress = progress;
        },
        onLeave: () => {
          if (this.currentState === 'SCRUB') {
                        this.transitionTo('FINAL_LOOP');
          }
        },
        onEnterBack: () => {
                    if (this.currentState === 'FINAL_LOOP') {
            this.handleEnterBackFromFinalLoop(autoScrollSpeed, autoScrollIdleTimeout);
          }
        }
      });

      this.introTimeline = gsap.timeline({
        onUpdate: () => {
          const progress = this.introTimeline.progress();
          const frame = Math.floor(
            introFrames.start + progress * (introFrames.end - introFrames.start)
          );
          this.renderFrame(frame);
        },
        onComplete: () => {
          this.renderFrame(introFrames.end);
          this.playUniverseTimeReveal();
          this.transitionTo('INTRO_LOOP');
        }
      });

      this.introTimeline.to({}, { duration: introDuration });
    }

    /**
     * 从 FINAL_LOOP 回滚时的处理
     */
    handleEnterBackFromFinalLoop(autoScrollSpeed, autoScrollIdleTimeout) {
      this.stopFinalLoopAnimation();
      this.currentState = 'SCRUB';

      this.playWithTimeRevealed = false;
      this.resetFinalTitlesAnimation();

      if (this.titleFinalSides) {
        gsap.set(this.titleFinalSides, { opacity: 0 });
      }
    }

    /**
     * 播放 Universe Time 标题 Line Reveal
     */
    playUniverseTimeReveal() {
      const universeTimeInners = this.titleUniverseTime.querySelectorAll('.yd-line-inner');

      gsap.to(this.titleUniverseTime, {
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        onStart: () => {
          gsap.set(this.titlePlayWithTime, { opacity: 0 });
          if (this.titleFinalSides) {
            gsap.set(this.titleFinalSides, { opacity: 0 });
          }
        }
      });

      gsap.fromTo(
        universeTimeInners,
        { yPercent: 100 },
        { yPercent: 0, duration: 1.0, stagger: 0.12, ease: 'power3.out', delay: 0.1 }
      );
    }

    /**
     * INTRO_LOOP 状态
     */
    enterIntroLoop() {
      
      // 派发事件通知下游 section (video-scrub) 可以开始初始化
      window.dispatchEvent(new CustomEvent('yd-product-sequence-ready', {
        detail: { state: 'INTRO_LOOP', timestamp: Date.now(), device: 'mobile' }
      }));

      const { introLoopFrames, loopDuration } = this.config;

      this.playWithTimeRevealed = false;
      this.resetFinalTitlesAnimation();

      this.resetTitlesForIntroLoop();

      this.introLoopTimeline = gsap.timeline({
        repeat: -1,
        yoyo: false,
        onUpdate: () => {
          if (this.currentState === 'INTRO_LOOP') {
            const progress = this.introLoopTimeline.progress();
            const frame = Math.floor(
              introLoopFrames.start + progress * (introLoopFrames.end - introLoopFrames.start)
            );
            this.renderFrame(frame);
          }
        }
      });

      this.introLoopTimeline.to({}, { duration: loopDuration });

      this.loader.continuePreload();
    }

    /**
     * 重置标题到 INTRO_LOOP 状态
     */
    resetTitlesForIntroLoop() {
      if (this.titleUniverseTime) {
        gsap.set(this.titleUniverseTime, { opacity: 1 });
      }
      if (this.titlePlayWithTime) {
        gsap.set(this.titlePlayWithTime, { opacity: 0 });
        const playWithTimeInners = this.titlePlayWithTime.querySelectorAll('.yd-line-inner');
        gsap.set(playWithTimeInners, { yPercent: 100 });
      }
      if (this.titleFinalSides) {
        gsap.set(this.titleFinalSides, { opacity: 0 });
      }
    }

    /**
     * SCRUB 状态
     */
    enterScrub() {
      
      this.playWithTimeRevealed = false;
      this.universeTimeRevealed = false;
      this.resetFinalTitlesAnimation();
    }

    /**
     * FINAL_LOOP 状态
     */
    enterFinalLoop() {
      
      gsap.set(this.titleUniverseTime, { opacity: 0 });
      gsap.set(this.titlePlayWithTime, { opacity: 0 });

      if (this.titleFinalSides) {
        gsap.to(this.titleFinalSides, {
          opacity: 1,
          duration: 0.8,
          ease: 'power2.out',
          onComplete: () => this.animateFinalTitles()
        });
      }

      this.startFinalLoopAnimation();
    }
  }

  /**
   * 初始化函数
   */
  async function initProductSequence() {
    // 防止 HotReload 导致的重复初始化
    if (window.ydProductSequenceStateMachine) {
            return;
    }

    const section = document.querySelector('#product-sequence-section');
    if (!section) {
      return;
    }

    const configData = section.dataset.config;
    if (!configData) {
            return;
    }

    const config = JSON.parse(configData);

    // UI 元素
    const loadingScreen = document.getElementById('loading-screen');
    const loadingProgress = document.getElementById('loading-progress');
    const loadingPercentage = document.getElementById('loading-percentage');

    // 检测设备类型
    const isDesktop = window.YDDevice ? window.YDDevice.isDesktop() : window.innerWidth >= 1024;
    const isMobile = window.innerWidth < 768;
    const basePath = isMobile && config.mobileFramePath
      ? config.mobileFramePath
      : config.framePath;

    
    try {
      // 1. 创建加载器
      const loader = new LocalSequenceLoader({
        totalFrames: config.totalFrames,
        basePath: basePath,
        avifPath: config.avifPath,
        fallbackPath: config.fallbackPath
      });

      // 2. 预加载优先帧
      await loader.preloadPriority(
        config.priorityFrames,
        (progress) => {
          const percent = Math.round(progress * 100);
          if (loadingProgress) {
            loadingProgress.style.width = percent + '%';
          }
          if (loadingPercentage) {
            loadingPercentage.textContent = percent + '%';
          }

          if (progress >= config.hideLoadingAt && loadingScreen) {
            loadingScreen.classList.add('fade-out');
          }
        }
      );

      // 3. 创建渲染器
      const renderer = new CanvasRenderer('#product-sequence-canvas');

      // 4. 根据设备类型选择状态机
      const stateMachine = isDesktop
        ? new ProductSequenceStateMachineDesktop(config, loader, renderer)
        : new ProductSequenceStateMachineMobile(config, loader, renderer);

      // 5. 启动 INTRO 状态
      stateMachine.transitionTo('INTRO');

      // 保存到全局以便调试
      window.ydProductSequenceStateMachine = stateMachine;

    } catch (error) {
      
      if (loadingScreen) {
        loadingScreen.classList.add('fade-out');
      }
    }
  }

  /**
   * 等待 GSAP 和 ScrollTrigger 加载
   */
  function waitForDependencies() {
    return new Promise((resolve, reject) => {
      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
                resolve();
        return;
      }

      const check = setInterval(() => {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          clearInterval(check);
          gsap.registerPlugin(ScrollTrigger);
                    resolve();
        }
      }, 50);

      setTimeout(() => {
        clearInterval(check);
                reject(new Error('GSAP/ScrollTrigger not loaded'));
      }, 10000);
    });
  }

  /**
   * 入口
   */
  async function init() {
    await waitForDependencies();
    await initProductSequence();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
